import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useCallback,
} from 'react';
import { Amplify, Auth, Hub } from 'aws-amplify';
import {
  AUTH_CONTEXT_HANDLERS, AuthContextAction, AuthContextState, AuthContextUser,
} from 'contexts/auth-context-model';
import { loadAmplifyConfig } from 'utils/endpoints-config';

import {
  getCustomPermissionsFunctions, getPermissions, readUser,
} from 'utils/api-client';
import { resolveFunctionPermissions } from 'utils/user-permissions';
import { hydrateSkillContextOverrides } from 'utils/skill-context';
import { clearAllImpersonation } from 'utils/impersonation';
import { Themes } from 'theme/themes';
import { clearAssistantHistory } from 'utils/lm-studio';
import { isProfileCreationResource, isProfileCreationVisible, withProfileCreationPermissions } from 'utils/demo-mode';

// One retry (after a short pause) for the calls everything else depends on —
// a single transient failure used to leave the user with no permissions at
// all, i.e. a blank sidebar until they reloaded.
async function withOneRetry<T>(load: () => Promise<T>, label: string): Promise<T> {
  try {
    return await load();
  } catch (e) {
    console.error(`Loading ${label} failed — retrying once`, e);
    await new Promise((resolve) => { setTimeout(resolve, 800); });
    return load();
  }
}

const handlers = {
  [AUTH_CONTEXT_HANDLERS.INITIALIZE]: (state: AuthContextState, action: AuthContextAction) => {
    if (action.payload) {
      const { idToken, user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        idToken,
        user,
      };
    }
    return {
      ...state,
      isLoading: false,
    };
  },
  [AUTH_CONTEXT_HANDLERS.FETCH_PERMISSIONS]: (
    state: AuthContextState,
    action: AuthContextAction,
  ): AuthContextState => {
    if (action.payload) {
      const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        roles, person_id, ownPermissions, realPermissions, subRoles, roleData, impersonatedRoles, impersonatedUser, impersonatorUsername, impersonatedPersonId, resources, theme, predefinedFunctions, filteredResources, permission_groups,
      } = action.payload;
      const currentUser = { ...state.user } as AuthContextUser;
      if (currentUser) {
        currentUser.roles = roles;
        currentUser.person_id = person_id;
        currentUser.ownPermissions = ownPermissions;
        currentUser.realPermissions = realPermissions;
        currentUser.subRoles = subRoles;
        currentUser.roleData = roleData;
        currentUser.impersonatedRoles = impersonatedRoles;
        currentUser.impersonatedUser = impersonatedUser;
        currentUser.impersonatorUsername = impersonatorUsername;
        currentUser.impersonatedPersonId = impersonatedPersonId;
        currentUser.resources = resources;
        currentUser.theme = theme;
        currentUser.predefinedFunctions = predefinedFunctions;
        currentUser.filteredResources = filteredResources;
        currentUser.permission_groups = permission_groups;
        return {
          ...state,
          user: currentUser,
          isLoading: false,
        };
      }
    }
    return state;
  },
  [AUTH_CONTEXT_HANDLERS.SIGN_OUT]: (state: AuthContextState): AuthContextState => ({
    ...state,
    isAuthenticated: false,
    isLoading: false,
    user: null,
  }),
};

const reducer = (state: AuthContextState, action: AuthContextAction): AuthContextState => {
  if (handlers[action.type]) {
    return handlers[action.type](state, action);
  }
  return state;
};

const initialState: AuthContextState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
};

// Converts a user from the Amplify library to a user with the tool's auth context model
const getAuthContextUserFromAmplifyUser = (currentAuthenticatedUser: any): AuthContextUser => ({
  email: currentAuthenticatedUser.attributes.email,
  person_id: '',
  roles: ['Inactive'],
  ownPermissions: {},
  realPermissions: {},
  subRoles: [],
  roleData: [],
  resources: [],
  theme: Themes.SYSTEM,
  predefinedFunctions: [],
  filteredResources: {},
  permission_groups: [],
});

// The role of this context is to propagate authentication state through the App tree.
export const AuthContext = createContext(initialState);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const initialized = useRef(false);

  const tryLoadUser = async () => {
    const config = await loadAmplifyConfig();
    Amplify.configure(config);

    try {
      // Load the currently authenticated user
      const res = await Auth.currentAuthenticatedUser();
      const user = getAuthContextUserFromAmplifyUser(res);
      dispatch({
        type: AUTH_CONTEXT_HANDLERS.INITIALIZE,
        payload: {
          idToken: res.signInUserSession.idToken.jwtToken,
          user,
        },
      });

      // Load the user's permissions, if the Auth.currentAuthenticatedUser
      // call succeeds (doesn't throw an exception because the user is not logged in)

      const userData = await withOneRetry(() => readUser(user.email), 'user');
      if (userData) {
        const userPermissions = await withOneRetry(() => getPermissions(), 'permissions');
        const predefinedFunctions = await getCustomPermissionsFunctions().catch(() => undefined);
        userData.permission_groups = userData.permission_groups ? userData.permission_groups : [];

        if (userPermissions) {
          // One batched request for every permission function; each resolves
          // (or fails) on its own, so one bad function can't blank the app.
          const merged = userPermissions.impersonation || userPermissions.permissions;
          const resolved = await resolveFunctionPermissions(merged);
          userData.realPermissions = merged;
          // Demo mode / builds without profile creation: the UI sees those
          // permissions as Deny, so everything they gate hides itself.
          userData.ownPermissions = withProfileCreationPermissions(resolved.ownPermissions);
          userData.filteredResources = Object.fromEntries(Object.entries(resolved.filteredResources)
            .filter(([name]) => isProfileCreationVisible() || !isProfileCreationResource(name)));
          userData.subRoles = userPermissions.subroles;
          userData.roleData = userPermissions.role_data;
          userData.impersonatedRoles = userPermissions.impersonated_roles;
          userData.impersonatedUser = userPermissions.impersonated_user;
          userData.impersonatorUsername = userPermissions.impersonator_username;
          userData.impersonatedPersonId = userPermissions.impersonated_person_id || undefined;
          userData.resources = isProfileCreationVisible()
            ? userPermissions.resources
            : (userPermissions.resources || []).filter((name: string) => !isProfileCreationResource(name));
          userData.predefinedFunctions = predefinedFunctions;
        }

        dispatch({
          type: AUTH_CONTEXT_HANDLERS.FETCH_PERMISSIONS,
          payload: userData,
        });

        // Fire-and-forget: skill-context overrides shadow the
        // bundled JSON, but the page is happy to render with the
        // bundled fallback if hydration is slow or fails.
        hydrateSkillContextOverrides();
      } else {
        console.error(`Failed to fetch permissions for user '${user.email}'`);
      }
    } catch (err: any) {
      if (err?.response?.status === '503') {
        if (window.location.pathname !== '/maintenance/') {
          window.location.href = '/maintenance/';
        }
      }
      console.error(`Loading auth context: '${err}'`);
      dispatch({
        type: AUTH_CONTEXT_HANDLERS.INITIALIZE,
      });
    }
  };

  const initialize = useCallback(async () => {
    const config = await loadAmplifyConfig();
    Amplify.configure(config);

    Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signIn') {
        // Wipe any impersonation from a prior session — a fresh login
        // should always land you in your own session, never inherit an
        // impersonation target from before sign-out.
        clearAllImpersonation();
        tryLoadUser();
      } else if (payload.event === 'signOut') {
        clearAllImpersonation();
        clearAssistantHistory(); // page-assistant chats can hold HQ data
        window.location.reload();
      }
    });

    tryLoadUser();
  }, []);

  useEffect(() => {
    // Prevent from calling twice in development mode with React.StrictMode enabled
    if (!initialized.current) {
      initialize();
    }
    return () => {
      initialized.current = true;
    };
  }, [initialize]);

  return (
    <AuthContext.Provider
      value={state}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthConsumer = AuthContext.Consumer;
export const useAuthContext = () => useContext(AuthContext);
